/**
 * Custom auth component for this project
 */
import { Auth, API } from 'aws-amplify';
import { EventBus, vuexErrorHandler } from '@switchsystems/coactive-vue-lib';

// eslint-disable-next-line import/no-extraneous-dependencies
import { AuthState } from '@aws-amplify/ui-components';
import { Ability } from '@casl/ability';

const API_NAME = 'users';
const API_VERSION = '/v1';

async function getApiInit() {
  return {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession()).getIdToken().getJwtToken()}`,
    },
  };
}

const modulestate = () => ({
  cognitoData: undefined,
  authState: undefined,
  rememberDevice: true,
  user: {},
  // we don't actually use ability within recruitment,
  // but it's useful to detect if we have a staff user object
  ability: new Ability([]),
});
const getters = {
  isSignedIn: (state) => state.authState === AuthState.SignedIn,
  getCognitoData: (state) => state.cognitoData,
  getAuthState: (state) => state.authState,
  user: (state) => state.user,
  getRememberDevice: (state) => state.rememberDevice,
  ability: (state) => state.ability,
};
const mutations = {
  changeAuthState(state, authState) {
    state.authState = authState;
  },
  changeCognitoData(state, cognitoData) {
    state.cognitoData = cognitoData;
  },
  updateUser(state, newUser) {
    state.user = newUser;
  },
  updateAbility(state, newAbility) {
    // CC-173 our recruitment users don't actually have abilities,
    // so any abilities set here means that the user is a staff entity from the
    // main coactive care system, so we should sign them out!
    if (newAbility.rules.length > 0) {
      console.log('Staff account detected. Logging out.');
      // emit event
      EventBus.$emit('toast:show', {
        title: 'Staff account detected',
        variant: 'warning',
        'auto-hide-delay': 5000,
        body: 'You should not browse the public site with your staff account active. Logging you out ...',
      });
      // shouldn't really call an action from within a mutation... but it seems to work
      this.dispatch('auth/logoutUser');
    } else {
      state.ability = newAbility;
    }
  },
};
const actions = {
  async onAuthUIStateChange({ commit, dispatch }, authState, authData) {
    commit('changeCognitoData', authData);
    commit('changeAuthState', authState);

    // if we've transitioned to a signed in state, fetch a new user object from the user api.
    if (authState === AuthState.SignedIn) {
      dispatch('fetchUser');
    }
  },
  async fetchUser({ commit }) {
    await API
      .get(API_NAME, `${API_VERSION}/users/me`, await getApiInit())
      .then((response) => {
        if (response.id) {
          commit('updateUser', response);
        }
      })
      .catch((error) => vuexErrorHandler(error));
  },
  async logoutUser({ commit }) {
    commit('updateUser', {});
    commit('changeAuthState', AuthState.signedOut);
    await Auth.signOut();
  },
  async updatePassword(context, params) {
    const currentUser = await Auth.currentAuthenticatedUser();
    return Auth.changePassword(
      currentUser,
      params.oldPassword,
      params.password,
    );
  },
};
export default {
  namespaced: true,
  mutations,
  getters,
  actions,
  state: modulestate,
};
